const OPTIONSIGHT_ALLOWED_RICH_TEXT_TAGS = new Set(['A', 'B', 'BR', 'EM', 'I', 'P', 'STRONG']);
const OPTIONSIGHT_PROGRESS_TTL_MS = 14 * 24 * 60 * 60 * 1000;
const OPTIONSIGHT_RETURN_POSITION_TTL_MS = 2 * 60 * 60 * 1000;
const OPTIONSIGHT_OPTION_HTML_ENTITIES = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
};

function decodeOptionSightOptionEntities(value) {
  let decoded = String(value || '');

  for (let pass = 0; pass < 5; pass += 1) {
    const next = decoded.replace(/&(?:#x[0-9a-f]+|#\d+|amp|apos|gt|lt|nbsp|quot);/gi, (entity) => {
      const key = entity.slice(1, -1).toLowerCase();
      if (Object.hasOwn(OPTIONSIGHT_OPTION_HTML_ENTITIES, key)) return OPTIONSIGHT_OPTION_HTML_ENTITIES[key];

      const isHex = key.startsWith('#x');
      const codePoint = Number.parseInt(key.slice(isHex ? 2 : 1), isHex ? 16 : 10);
      if (!Number.isInteger(codePoint) || codePoint < 0 || codePoint > 0x10ffff) return entity;

      try {
        return String.fromCodePoint(codePoint);
      } catch {
        return entity;
      }
    });
    if (next === decoded) break;
    decoded = next;
  }

  return decoded;
}

function isOptionSightImagePreviewTarget(choice, target) {
  return target instanceof Node && (choice.contains(target) || choice.control === target);
}

if (!customElements.get('optionsight-options')) {
  customElements.define(
    'optionsight-options',
    class OptionSightOptions extends HTMLElement {
      constructor() {
        super();
        this.inputs = [];
        this.form = null;
        this.loaded = false;
        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.onWindowResize = this.onWindowResize.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.onProductFormChange = this.onProductFormChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onDirectCheckout = this.onDirectCheckout.bind(this);
        this.purchaseInFlight = false;
        this.directCheckoutButton = null;
      }

      connectedCallback() {
        if (!this.initialized) {
          this.initialized = true;
          this.grid = this.querySelector('[data-optionsight-grid]');
          this.error = this.querySelector('[data-optionsight-error]');
          this.priceSummary = this.querySelector('[data-optionsight-price-summary]');
          this.priceSummaryAmount = this.querySelector('[data-optionsight-price-summary-amount]');
          this.variantPricesMinor = this.readVariantPricesMinor();
          this.variantPrices = new Map(
            [...this.variantPricesMinor.entries()].map(([variantId, priceMinor]) => [variantId, priceMinor / 100])
          );
          this.setProductSubmitDisabled(true);
          this.loadOptions();
        }

        this.bindLifecycleListeners();
        if (this.loaded) {
          this.bindProductForm();
          this.update();
        }
      }

      bindLifecycleListeners() {
        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
        window.addEventListener('resize', this.onWindowResize);
        window.addEventListener('pageshow', this.onPageShow);
        document.addEventListener('change', this.onProductFormChange);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        window.removeEventListener('resize', this.onWindowResize);
        window.removeEventListener('pageshow', this.onPageShow);
        document.removeEventListener('change', this.onProductFormChange);
        this.form?.removeEventListener('submit', this.onSubmit, true);
        this.directCheckoutButton?.removeEventListener('click', this.onDirectCheckout, true);
        this.form = null;
        this.directCheckoutButton = null;
      }

      async loadOptions() {
        const urls = (this.dataset.optionConfigUrls || '')
          .split(',')
          .map((url) => url.trim())
          .filter(Boolean);

        if (!this.grid || urls.length === 0) {
          this.renderLoadError();
          return;
        }

        this.setAttribute('aria-busy', 'true');

        try {
          const configs = await Promise.all(
            urls.map(async (url) => {
              const response = await fetch(url);
              if (!response.ok) throw new Error(`Failed to load ${url}`);
              return response.json();
            })
          );
          const rows = configs.flatMap((config) => config.rows || []);
          this.validateProductOptionRows(rows);
          this.renderRows(rows);
          this.inputs = Array.from(this.querySelectorAll('[data-optionsight-input]'));
          this.applyFreeFallbackDefaults();
          const restoredProgress = this.restoreSavedProgress();
          this.loaded = true;
          this.removeAttribute('aria-busy');
          if (this.isConnected) {
            this.bindProductForm();
            this.update();
          }
          if (restoredProgress?.returnPending) this.restoreReturnPosition(restoredProgress);
          this.dispatchEvent(new CustomEvent('optionsight:ready', { bubbles: true }));
        } catch (error) {
          console.error(error);
          this.emitIntegrityDiagnostic('configuration', 'blocked', {
            reason: this.getIntegrityErrorReason(error, 'option_configuration_unavailable'),
          });
          this.renderLoadError();
        }
      }

      renderLoadError() {
        this.loaded = false;
        this.removeAttribute('aria-busy');
        this.classList.add('optionsight-options--error');
        if (this.error) {
          this.error.hidden = false;
          this.error.textContent = 'Custom options are temporarily unavailable. Please refresh or contact support.';
        }
        this.setProductSubmitDisabled(true);
      }

      validateProductOptionRows(rows) {
        const invalidProductRow = (Array.isArray(rows) ? rows : []).find((row) => {
          if (this.getRowKind(row?.selector) !== 'product') return false;
          const variantId = String(row.selector || '').replace(/^variant:/i, '').trim();
          const numericVariantId = Number(variantId);
          return (
            !Number.isSafeInteger(numericVariantId)
            || numericVariantId <= 0
            || !this.variantPricesMinor?.has(variantId)
          );
        });
        if (invalidProductRow) throw this.createIntegrityError('option_config_variant_unavailable');
      }

      renderRows(rows) {
        const fragment = document.createDocumentFragment();
        let activeRecord = null;
        let optionIndex = 0;

        const flushGroup = () => {
          if (!activeRecord) return;
          fragment.append(this.createOptionGroup(activeRecord));
          activeRecord = null;
        };

        rows.forEach((row) => {
          const kind = this.getRowKind(row.selector);
          const label = String(row.label || '').trim();
          const group = String(row.group || '').trim();

          if (this.isChoiceKind(kind) && label) {
            if (!activeRecord || activeRecord.group !== (group || 'Options')) {
              flushGroup();
              activeRecord = { group: group || 'Options', rows: [] };
            }
            activeRecord.rows.push({ row, kind, index: optionIndex });
            optionIndex += 1;
            return;
          }

          if (activeRecord && group && activeRecord.group === group) {
            activeRecord.rows.push({ row, kind, index: optionIndex });
            return;
          }

          flushGroup();
          const display = this.createDisplayRow(row, kind);
          if (display) fragment.append(display);
        });

        flushGroup();
        this.grid.replaceChildren(fragment);
      }

      createDisplayRow(row, kind) {
        const label = String(row.label || '').trim();
        const displayLabel = String(row.displayLabel || label).trim();
        if (kind === 'divider') {
          const divider = document.createElement('hr');
          divider.className = 'optionsight-options__divider';
          return divider;
        }
        if (!label || !['heading', 'note'].includes(kind)) return null;

        const element = document.createElement('div');
        element.className = `optionsight-options__info optionsight-options__info--${kind}`;
        if (kind === 'note') {
          const muted = this.normalize(row.styleToken) === 'helptext';
          element.classList.add(`optionsight-options__info--note-${muted ? 'muted' : 'body'}`);
        }
        this.setSafeHtml(element, row.richTextHtml || row.sectionIntroHtml, displayLabel);
        return element;
      }

      createOptionGroup(record) {
        const group = document.createElement('section');
        const titleRow = document.createElement('div');
        const label = document.createElement('span');
        const groupPrice = document.createElement('span');
        const selected = document.createElement('div');
        const choices = document.createElement('div');
        const conflictMessage = document.createElement('div');
        const notes = document.createElement('div');
        const body = document.createElement('div');
        const choiceRows = record.rows.filter(({ kind, row }) => this.isChoiceKind(kind) && row.label);
        const metadata = choiceRows[0]?.row || record.rows[0]?.row || {};
        const presentation = this.getGroupPresentation(metadata, record.group);
        const required = choiceRows.some(({ row }) => this.normalize(row.requiredMark) === 'required');
        const groupId = `OptionSightGroup-${this.safeId(record.group)}-${this.safeId(metadata.elementId || '')}`;

        group.className = `optionsight-options__group optionsight-options__group--${presentation}`;
        group.dataset.optionsightOptionGroup = record.group;
        group.dataset.optionsightPresentation = presentation;
        group.setAttribute('data-optionsight-option-required', String(required));
        if (metadata.elementId) group.dataset.optionElementId = metadata.elementId;
        if (metadata.visibilityRule) group.dataset.optionVisibilityRule = JSON.stringify(metadata.visibilityRule);

        titleRow.className = 'optionsight-options__group-heading';
        label.className = 'optionsight-options__group-label';
        label.textContent = metadata.groupDisplayLabel || this.getGroupTitle(record.group);
        groupPrice.className = 'optionsight-options__group-price';
        groupPrice.dataset.optionsightOptionGroupPrice = '';
        groupPrice.hidden = true;
        label.append(document.createTextNode(' '), groupPrice);
        if (required) label.classList.add('is-required');

        if (this.normalize(record.group) !== 'appearance & functions') titleRow.append(label);

        selected.className = 'optionsight-options__selected-value';
        selected.dataset.optionsightOptionSelected = record.group;

        choices.className = `optionsight-options__choices optionsight-options__choices--${presentation}`;
        choices.setAttribute('role', 'list');
        conflictMessage.className = 'optionsight-options__conflict-message';
        conflictMessage.id = `${groupId}-conflict`;
        conflictMessage.dataset.optionsightConflictMessage = '';
        conflictMessage.setAttribute('role', 'status');
        conflictMessage.hidden = true;
        notes.className = 'optionsight-options__notes';
        body.className = 'optionsight-options__group-body';
        body.id = groupId;
        body.dataset.optionsightGroupBody = '';

        record.rows.forEach(({ row, kind, index }) => {
          if (this.isChoiceKind(kind) && row.label) {
            choices.append(this.createOptionItem(row, kind, index, presentation));
            return;
          }

          const note = this.createDisplayRow(row, kind);
          if (note) notes.append(note);
        });

        body.append(choices, conflictMessage);
        if (notes.children.length) body.append(notes);
        group.append(titleRow, selected, body);
        return group;
      }

      createOptionItem(row, kind, index, presentation) {
        const item = document.createElement('div');
        const input = document.createElement('input');
        const label = document.createElement('label');
        const choiceId = `OptionSight-${this.safeId(this.dataset.optionSetIds || 'set')}-${index}`;
        const mode = this.normalize(row.mode) || (kind === 'product' ? 'multi' : 'single');
        const hasImage = Boolean(row.imageUrl);
        const elementType = this.normalize(row.elementType || row.styleToken);
        const isButton = elementType === 'buttons' || (!hasImage && presentation === 'horizontal');
        const separatesImageControl = hasImage && presentation === 'vertical';
        const priceText = this.getOptionPriceText(row, kind);
        const optionLabel = decodeOptionSightOptionEntities(row.label).trim();
        const optionGroup = decodeOptionSightOptionEntities(row.group).trim();
        const displayLabel = decodeOptionSightOptionEntities(row.displayLabel || optionLabel).trim();

        item.className = [
          'optionsight-options__item',
          hasImage ? 'optionsight-options__item--image' : 'optionsight-options__item--text',
          isButton ? 'optionsight-options__item--button' : '',
          presentation === 'vertical' ? 'optionsight-options__item--vertical' : '',
          separatesImageControl ? 'optionsight-options__item--separate-copy' : '',
        ]
          .filter(Boolean)
          .join(' ');
        item.setAttribute('role', 'listitem');

        input.className = 'optionsight-options__input visually-hidden';
        input.id = choiceId;
        input.type = mode === 'multi' ? 'checkbox' : 'radio';
        input.name = `optionsight-${this.safeId(this.dataset.optionSetIds || 'set')}-${this.safeId(row.elementId || row.group)}`;
        input.checked = this.normalize(row.defaultMark) === 'default';
        input.dataset.optionsightInput = '';
        input.dataset.optionLabel = optionLabel;
        input.dataset.optionDisplayLabel = displayLabel;
        input.dataset.optionKind = kind;
        input.dataset.optionVariantId = kind === 'product' ? String(row.selector || '').replace(/^variant:/i, '') : '';
        input.dataset.optionGroup = optionGroup;
        input.dataset.optionDisplayGroup = this.getGroupTitle(optionGroup);
        input.dataset.optionMode = mode;
        input.dataset.optionConflicts = decodeOptionSightOptionEntities(row.conflicts).trim();
        input.dataset.optionImageUrl = String(row.imageUrl || '').trim();
        input.dataset.optionPriceText = priceText;
        input.dataset.optionDefaultMark = String(row.defaultMark || '').trim();
        input.dataset.optionRequiredMark = String(row.requiredMark || '').trim();
        input.dataset.optionElementId = String(row.elementId || '').trim();
        input.dataset.optionElementType = String(row.elementType || '').trim();
        input.dataset.optionLabelOnCart = decodeOptionSightOptionEntities(row.labelOnCart).trim();
        input.dataset.optionDeselectNotAllowed = String(row.deselectNotAllowed || '').trim();

        label.className = 'optionsight-options__choice';
        label.htmlFor = choiceId;
        label.setAttribute('aria-label', [displayLabel, this.formatPrice(priceText)].filter(Boolean).join(' '));

        if (hasImage && this.normalize(row.tooltipStyle).includes('zoom')) {
          label.setAttribute('aria-expanded', 'false');
        }

        if (hasImage) label.append(this.createSwatch(row, priceText, displayLabel));

        let copy = null;
        if (!hasImage || presentation === 'vertical' || isButton) {
          copy = document.createElement('span');
          const title = document.createElement('span');
          copy.className = 'optionsight-options__choice-copy';
          title.className = 'optionsight-options__choice-title';
          title.textContent = displayLabel;
          copy.append(title);

          const price = this.formatPrice(priceText);
          if (price) {
            const priceElement = document.createElement('span');
            priceElement.className = 'optionsight-options__price';
            priceElement.textContent = price;
            copy.append(document.createTextNode(' '), priceElement);
          }

          if (row.choiceHelpHtml || row.helpText) {
            const help = document.createElement('span');
            help.className = 'optionsight-options__choice-help';
            this.setSafeHtml(help, row.choiceHelpHtml, row.helpText);
            copy.append(help);
          }
          if (!separatesImageControl) label.append(copy);
        }

        item.append(input, label);
        if (copy && separatesImageControl) item.append(copy);
        return item;
      }

      applyFreeFallbackDefaults() {
        this.querySelectorAll('[data-optionsight-option-group]').forEach((group) => {
          const inputs = Array.from(group.querySelectorAll('[data-optionsight-input]'));
          if (inputs.length === 0 || inputs.some((input) => input.checked)) return;

          const firstInput = inputs[0];
          if (this.parsePriceAmount(firstInput.dataset.optionPriceText) > 0) return;

          firstInput.checked = true;
          firstInput.dataset.optionDefaultSource = 'free-fallback';
        });
      }

      createSwatch(row, priceText, displayLabel) {
        const swatch = document.createElement('span');
        const image = document.createElement('img');
        const tooltip = document.createElement('span');
        const tooltipTitle = document.createElement('span');
        const tooltipImage = document.createElement('img');
        const [swatchWidth, swatchHeight] = this.parseDimensions(row.swatchSize, 70, 70);
        const [tooltipWidth, tooltipHeight] = this.parseDimensions(row.tooltipImageSize, 150, 150);

        swatch.className = 'optionsight-options__swatch';
        swatch.style.setProperty('--optionsight-swatch-width', `${swatchWidth}px`);
        swatch.style.setProperty('--optionsight-swatch-height', `${swatchHeight}px`);
        image.src = row.imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = swatchWidth;
        image.height = swatchHeight;
        swatch.append(image);

        if (this.normalize(row.tooltipStyle).includes('zoom')) {
          tooltip.className = 'optionsight-options__tooltip';
          tooltip.setAttribute('role', 'tooltip');
          tooltip.style.setProperty('--optionsight-tooltip-width', `${tooltipWidth}px`);
          tooltip.style.setProperty('--optionsight-tooltip-height', `${tooltipHeight}px`);
          tooltipTitle.className = 'optionsight-options__tooltip-title';
          tooltipTitle.textContent = this.formatPreviewLabel(displayLabel, priceText);
          tooltipImage.src = row.imageUrl;
          tooltipImage.alt = '';
          tooltipImage.loading = 'lazy';
          tooltipImage.width = tooltipWidth;
          tooltipImage.height = tooltipHeight;
          tooltip.append(tooltipImage, tooltipTitle);
          swatch.append(tooltip);
        }

        return swatch;
      }

      onClick(event) {
        const helpLink = event.target.closest('a[href]');
        if (helpLink && this.contains(helpLink)) {
          this.rememberProgressForReturn(helpLink);
          return;
        }

        const previewChoice = event.target.closest('.optionsight-options__choice');
        const previewInput = previewChoice?.control;
        const shouldKeepRequiredChoice = this.shouldKeepRequiredChoiceSelected(previewInput);
        if (shouldKeepRequiredChoice) event.preventDefault();

        if (this.shouldClearOptionalSingleChoice(previewInput)) {
          event.preventDefault();
          this.clearOptionalSingleChoice(previewInput);
          this.closeImagePreviews();
          return;
        }

        const tooltip = previewChoice?.querySelector('.optionsight-options__tooltip');
        if (previewChoice && tooltip && this.contains(previewChoice) && this.isMobileImagePreview()) {
          if (previewInput?.checked && !shouldKeepRequiredChoice) {
            this.closeImagePreviews();
            return;
          }

          const shouldOpen = !previewChoice.classList.contains('is-preview-open');
          this.closeImagePreviews(previewChoice);
          previewChoice.classList.toggle('is-preview-open', shouldOpen);
          previewChoice.setAttribute('aria-expanded', String(shouldOpen));
          if (shouldOpen) {
            this.positionMobileImagePreview(previewChoice, tooltip);
          } else {
            this.resetImagePreviewPosition(tooltip);
          }
          return;
        }
      }

      onDocumentClick(event) {
        const link = event.target.closest?.('a[href]');
        if (this.isCartPageLink(link)) this.rememberProgressForCartReturn();
        const openChoice = this.querySelector('.optionsight-options__choice.is-preview-open');
        if (!openChoice) return;
        if (isOptionSightImagePreviewTarget(openChoice, event.target)) return;
        this.closeImagePreviews();
      }

      isCartPageLink(link) {
        if (!link) return false;
        try {
          const url = new URL(link.href, window.location.href);
          return url.origin === window.location.origin && /\/cart\/?$/.test(url.pathname);
        } catch {
          return false;
        }
      }

      onDocumentKeydown(event) {
        if (event.key === 'Escape') {
          this.closeImagePreviews();
          return;
        }

        const input = event.target.closest?.('[data-optionsight-input]');
        if (
          (event.key === ' ' || event.code === 'Space') &&
          this.contains(input) &&
          this.shouldKeepRequiredChoiceSelected(input)
        ) {
          event.preventDefault();
          return;
        }

        if (
          (event.key === ' ' || event.code === 'Space') &&
          this.contains(input) &&
          this.shouldClearOptionalSingleChoice(input)
        ) {
          event.preventDefault();
          this.clearOptionalSingleChoice(input);
        }
      }

      onWindowResize() {
        if (!this.isMobileImagePreview()) {
          this.closeImagePreviews();
          return;
        }
        const choice = this.querySelector('.optionsight-options__choice.is-preview-open');
        const tooltip = choice?.querySelector('.optionsight-options__tooltip');
        if (choice && tooltip) this.positionMobileImagePreview(choice, tooltip);
      }

      onPageShow() {
        if (!this.loaded) return;
        const restoredProgress = this.restoreSavedProgress();
        if (!restoredProgress) return;
        this.update();
        if (restoredProgress.returnPending) this.restoreReturnPosition(restoredProgress);
      }

      onProductFormChange(event) {
        if (!this.loaded || event.target?.form !== this.form) return;
        if (!['id', 'quantity', 'selling_plan'].includes(event.target.name)) return;
        const checked = this.inputs.filter((input) => input.checked && !input.disabled);
        const completion = this.getRequiredCompletion(checked);
        this.setProductSubmitDisabled(!completion.complete);
      }

      isMobileImagePreview() {
        return window.matchMedia?.('(max-width: 749px)').matches ?? window.innerWidth <= 749;
      }

      positionMobileImagePreview(choice, tooltip) {
        const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
        const viewportMargin = 12;
        tooltip.style.removeProperty('--optionsight-tooltip-shift-x');
        tooltip.classList.remove('is-below');

        let rect = tooltip.getBoundingClientRect();
        let shiftX = 0;
        if (rect.left < viewportMargin) shiftX += viewportMargin - rect.left;
        if (rect.right + shiftX > viewportWidth - viewportMargin) {
          shiftX -= rect.right + shiftX - (viewportWidth - viewportMargin);
        }
        tooltip.style.setProperty('--optionsight-tooltip-shift-x', `${Math.round(shiftX)}px`);

        rect = tooltip.getBoundingClientRect();
        if (rect.top < viewportMargin) tooltip.classList.add('is-below');
        choice.setAttribute('aria-expanded', 'true');
      }

      resetImagePreviewPosition(tooltip) {
        tooltip?.style.removeProperty('--optionsight-tooltip-shift-x');
        tooltip?.classList.remove('is-below');
      }

      closeImagePreviews(except = null) {
        this.querySelectorAll('.optionsight-options__choice.is-preview-open').forEach((choice) => {
          if (choice === except) return;
          choice.classList.remove('is-preview-open');
          choice.setAttribute('aria-expanded', 'false');
          this.resetImagePreviewPosition(choice.querySelector('.optionsight-options__tooltip'));
        });
      }

      onChange(event) {
        const input = event.target.closest('[data-optionsight-input]');
        if (!input || !this.contains(input)) return;
        this.uncheckConflictingOptions(input);
        this.update();
        this.saveProgress();
      }

      shouldClearOptionalSingleChoice(input) {
        if (!input?.checked || this.normalize(input.dataset.optionMode) !== 'single') return false;
        const group = input.closest('[data-optionsight-option-group]');
        // The visible required star is the shopper-facing contract; imported Globo deselect flags are not consistent.
        return group?.dataset.optionsightOptionRequired !== 'true';
      }

      shouldKeepRequiredChoiceSelected(input) {
        if (!input?.checked) return false;
        const group = input.closest('[data-optionsight-option-group]');
        if (group?.dataset.optionsightOptionRequired !== 'true') return false;

        return !Array.from(group.querySelectorAll('[data-optionsight-input]')).some(
          (candidate) => candidate !== input && candidate.checked && !candidate.disabled
        );
      }

      clearOptionalSingleChoice(input) {
        input.checked = false;
        this.update();
        this.saveProgress();
      }

      readVariantPrices() {
        return new Map(
          [...this.readVariantPricesMinor().entries()].map(([variantId, priceMinor]) => [variantId, priceMinor / 100])
        );
      }

      readVariantPricesMinor() {
        const source = this.querySelector('[data-optionsight-variant-prices]');
        if (!source) return new Map();
        try {
          const rows = JSON.parse(source.textContent || '[]');
          return new Map(
            rows
              .filter((row) => row.available !== false)
              .map((row) => [String(row.id || ''), Number(row.priceMinor)])
              .filter(([id, priceMinor]) => id && Number.isSafeInteger(priceMinor) && priceMinor >= 0)
          );
        } catch {
          return new Map();
        }
      }

      getOptionPriceText(row, kind) {
        if (kind !== 'product') return String(row.price || '').trim();
        const variantId = String(row.selector || '').replace(/^variant:/i, '');
        if (!this.variantPrices?.has(variantId)) return String(row.price || '').trim();
        return String(this.variantPrices.get(variantId));
      }

      getProgressStorageKey() {
        const productKey = String(this.dataset.productId || window.location.pathname || '').trim();
        return `optionsight:progress:v2:${productKey}:${this.dataset.optionSetIds || ''}`;
      }

      getLegacyProgressStorageKey() {
        return `optionsight:progress:${window.location.pathname}:${this.dataset.optionSetIds || ''}`;
      }

      getInputProgressKey(input) {
        return [
          input.dataset.optionElementId,
          input.dataset.optionGroup,
          input.dataset.optionLabel,
          input.dataset.optionVariantId,
        ]
          .map((value) => String(value || '').trim())
          .join('::');
      }

      getGroupProgressKey(group) {
        return [group.dataset.optionElementId, group.dataset.optionsightOptionGroup]
          .map((value) => String(value || '').trim())
          .join('::');
      }

      readProgress() {
        const currentKey = this.getProgressStorageKey();
        const keys = [currentKey, this.getLegacyProgressStorageKey()];

        for (const storageName of ['localStorage', 'sessionStorage']) {
          for (const key of keys) {
            try {
              const storage = window[storageName];
              const raw = storage?.getItem(key);
              if (!raw) continue;
              const progress = JSON.parse(raw);
              const savedAt = Number(progress?.savedAt);
              if (!Number.isFinite(savedAt) || Date.now() - savedAt > OPTIONSIGHT_PROGRESS_TTL_MS) {
                storage.removeItem(key);
                continue;
              }
              if (key !== currentKey || storageName !== 'localStorage') this.writeProgress(progress);
              return progress;
            } catch {
              // Storage can be unavailable or contain stale data; try the next available source.
            }
          }
        }

        return null;
      }

      writeProgress(progress) {
        const serialized = JSON.stringify(progress);
        for (const storageName of ['localStorage', 'sessionStorage']) {
          try {
            window[storageName].setItem(this.getProgressStorageKey(), serialized);
            return;
          } catch {
            // Fall back to session storage when durable storage is unavailable.
          }
        }
      }

      saveProgress({ returnPending = false, returnAnchor = null } = {}) {
        if (!this.inputs.length) return;
        const selected = this.inputs.filter((input) => input.checked).map((input) => this.getInputProgressKey(input));
        this.writeProgress({ selected, returnPending, returnAnchor, savedAt: Date.now() });
      }

      rememberProgressForReturn(link) {
        if (link.target === '_blank' || /^(mailto:|tel:|#)/i.test(link.getAttribute('href') || '')) return;
        // The shared product-detail transaction owns help-link anchors. Keeping
        // a second OptionSight scroll target makes Safari apply two competing
        // corrections after pageshow, which is visible as a small jump.
        this.saveProgress();
      }

      rememberProgressForCartReturn() {
        this.saveProgress({
          returnPending: true,
          returnAnchor: {
            target: 'optionsight',
            viewportTop: this.isMobileImagePreview() ? 80 : 110,
            scrollY: window.scrollY,
          },
        });
      }

      restoreSavedProgress() {
        const progress = this.readProgress();
        if (!Array.isArray(progress?.selected)) return null;
        const selected = new Set(progress.selected);
        this.inputs.forEach((input) => {
          input.checked = selected.has(this.getInputProgressKey(input));
        });
        this.applyFreeFallbackDefaults();
        const shouldRestoreReturnPosition =
          progress.returnPending
          && progress.returnAnchor?.target === 'optionsight'
          && Date.now() - Number(progress.savedAt) <= OPTIONSIGHT_RETURN_POSITION_TTL_MS;
        if (progress.returnPending) this.saveProgress({ returnPending: false });
        return { ...progress, returnPending: shouldRestoreReturnPosition };
      }

      restoreReturnPosition(progress) {
        const anchorState = progress?.returnAnchor;
        const restore = () => {
          if (anchorState?.target === 'optionsight' && Number.isFinite(anchorState.viewportTop)) {
            window.scrollBy({ top: this.getBoundingClientRect().top - anchorState.viewportTop, behavior: 'auto' });
            return;
          }
          const anchor = Array.from(this.querySelectorAll('[data-optionsight-option-group]')).find(
            (group) => this.getGroupProgressKey(group) === anchorState?.key
          );
          if (anchor && Number.isFinite(anchorState?.viewportTop)) {
            window.scrollBy({ top: anchor.getBoundingClientRect().top - anchorState.viewportTop, behavior: 'auto' });
          } else if (Number.isFinite(anchorState?.scrollY)) {
            window.scrollTo({ top: anchorState.scrollY, behavior: 'auto' });
          }
        };
        requestAnimationFrame(() => requestAnimationFrame(restore));
        window.setTimeout(restore, 250);
      }

      update() {
        this.applyVisibilityRules();
        const initiallyChecked = this.inputs.filter((input) => input.checked && !input.disabled);
        const conflictSources = new Map();
        const groupConflicts = new Map();

        initiallyChecked.forEach((input) => {
          const sourceLabel = input.dataset.optionDisplayLabel || input.dataset.optionLabel || '';
          this.parseConflicts(input).forEach((conflict) => {
            const sources = conflictSources.get(conflict) || [];
            if (sourceLabel && !sources.includes(sourceLabel)) sources.push(sourceLabel);
            conflictSources.set(conflict, sources);
          });
        });

        this.inputs.forEach((input) => {
          const group = input.closest('[data-optionsight-option-group]');
          const hidden = Boolean(group?.hidden);
          const conflictLabels = conflictSources.get(this.normalize(input.dataset.optionLabel)) || [];
          const blocked = !hidden && conflictLabels.length > 0 && !input.checked;
          const item = input.closest('.optionsight-options__item');
          const label = item?.querySelector('.optionsight-options__choice');
          const message = group?.querySelector('[data-optionsight-conflict-message]');
          input.disabled = hidden || blocked;
          input.toggleAttribute('aria-disabled', hidden || blocked);
          item?.classList.toggle('is-conflicted', blocked);
          if (blocked && group) {
            const sourceKey = conflictLabels.join('\u0000');
            const records = groupConflicts.get(group) || new Map();
            const record = records.get(sourceKey) || { blockedLabels: [], sourceLabels: conflictLabels };
            const blockedLabel = input.dataset.optionDisplayLabel || input.dataset.optionLabel || '';
            if (blockedLabel && !record.blockedLabels.includes(blockedLabel)) record.blockedLabels.push(blockedLabel);
            records.set(sourceKey, record);
            groupConflicts.set(group, records);
          }
          if (label && message) {
            if (blocked) label.setAttribute('aria-describedby', message.id);
            else label.removeAttribute('aria-describedby');
          }
        });

        this.querySelectorAll('[data-optionsight-option-group]').forEach((group) => {
          const message = group.querySelector('[data-optionsight-conflict-message]');
          if (!message) return;
          const records = [...(groupConflicts.get(group)?.values() || [])];
          message.textContent = records
            .map(({ blockedLabels, sourceLabels }) => {
              const blocked = this.formatOptionList(blockedLabels);
              const sources = this.formatOptionList(sourceLabels);
              const blockedVerb = blockedLabels.length === 1 ? 'is' : 'are';
              const sourceVerb = sourceLabels.length === 1 ? 'is' : 'are';
              return `${blocked} ${blockedVerb} unavailable while ${sources} ${sourceVerb} selected.`;
            })
            .join(' ');
          message.hidden = records.length === 0;
        });

        const checked = this.inputs.filter((input) => input.checked && !input.disabled);
        this.updateSelectedValues(checked);
        this.updatePriceSummary(checked);
        const completion = this.getRequiredCompletion(checked);
        this.setProductSubmitDisabled(!this.loaded || !completion.complete);
        this.toggleAttribute('data-optionsight-complete', completion.complete);
      }

      applyVisibilityRules() {
        const selected = this.getSelectedValuesByElementId();
        this.querySelectorAll('[data-option-visibility-rule]').forEach((group) => {
          const rule = this.parseVisibilityRule(group.dataset.optionVisibilityRule);
          const visible = this.evaluateVisibilityRule(rule, selected);
          group.hidden = !visible;
          group.setAttribute('aria-hidden', String(!visible));
        });
      }

      getSelectedValuesByElementId() {
        const values = new Map();
        this.inputs.forEach((input) => {
          if (!input.checked || input.disabled) return;
          const elementId = input.dataset.optionElementId || '';
          if (!elementId) return;
          values.set(elementId, [...(values.get(elementId) || []), input.dataset.optionLabel || '']);
        });
        return values;
      }

      evaluateVisibilityRule(rule, selectedValues) {
        if (!rule || !Array.isArray(rule.whens) || rule.whens.length === 0) return true;
        const matches = rule.whens.map((condition) => {
          const values = selectedValues.get(String(condition.select || '').trim()) || [];
          if (String(condition.where || '').toUpperCase() !== 'EQUALS') return false;
          return values.some((value) => this.normalize(value) === this.normalize(condition.value));
        });
        const matched = rule.match === 'any' ? matches.some(Boolean) : matches.every(Boolean);
        return rule.display === 'hide' ? !matched : matched;
      }

      updateSelectedValues(checkedInputs) {
        const groups = new Map();
        checkedInputs.forEach((input) => {
          const group = input.dataset.optionGroup || '';
          groups.set(group, [...(groups.get(group) || []), input]);
        });

        this.querySelectorAll('[data-optionsight-option-group]').forEach((groupElement) => {
          const group = groupElement.dataset.optionsightOptionGroup || '';
          const selectedInputs = groups.get(group) || [];
          const selected = selectedInputs
            .map((input) => input.dataset.optionDisplayLabel || input.dataset.optionLabel || '')
            .filter(Boolean);
          const value = groupElement.querySelector('[data-optionsight-option-selected]');
          const price = groupElement.querySelector('[data-optionsight-option-group-price]');
          const total = selectedInputs.reduce(
            (sum, input) => sum + this.parsePriceAmount(input.dataset.optionPriceText),
            0
          );
          if (value) {
            value.textContent = selected.join(', ');
            value.hidden = selected.length === 0;
          }
          if (price) {
            price.textContent = total > 0 ? `(+ ${this.formatMoney(total)} ${this.getCurrency()})` : '';
            price.hidden = total <= 0;
          }
          groupElement.classList.toggle('is-complete', selected.length > 0);
        });
      }

      updatePriceSummary(checkedInputs) {
        const totalMinor = this.getChargedAddOnTotalMinor(checkedInputs);
        const total = totalMinor / 100;
        if (this.priceSummary && this.priceSummaryAmount) {
          this.priceSummary.hidden = totalMinor <= 0;
          this.priceSummaryAmount.textContent = totalMinor > 0 ? `${this.formatMoney(total)} ${this.getCurrency()}` : '';
        }
        this.dispatchEvent(
          new CustomEvent('optionsight:price-change', {
            bubbles: true,
            detail: { addOnTotalMinor: totalMinor },
          })
        );
      }

      getChargedAddOnTotalMinor(checkedInputs) {
        const chargedVariantIds = new Set();
        return checkedInputs.reduce((totalMinor, input) => {
          const variantId = String(input.dataset.optionVariantId || '').trim();
          if (!variantId || chargedVariantIds.has(variantId)) return totalMinor;
          chargedVariantIds.add(variantId);

          const liveAmount = this.variantPrices?.get(variantId);
          const amount = Number.isFinite(liveAmount)
            ? liveAmount
            : this.normalize(input.dataset.optionKind) === 'product'
              ? 0
              : this.parsePriceAmount(input.dataset.optionPriceText);
          return totalMinor + Math.round(amount * 100);
        }, 0);
      }

      getRequiredCompletion(checkedInputs) {
        const checkedGroups = new Set(checkedInputs.map((input) => this.normalize(input.dataset.optionGroup)));
        const requiredGroups = Array.from(
          this.querySelectorAll('[data-optionsight-option-group][data-optionsight-option-required="true"]')
        )
          .filter((group) => !group.hidden)
          .map((group) => this.normalize(group.dataset.optionsightOptionGroup))
          .filter(Boolean);
        const completed = requiredGroups.filter((group) => checkedGroups.has(group)).length;
        return { completed, required: requiredGroups.length, complete: completed === requiredGroups.length };
      }

      bindProductForm() {
        const form = document.getElementById(this.dataset.formId || '');
        if (!form) return;
        this.form?.removeEventListener('submit', this.onSubmit, true);
        this.directCheckoutButton?.removeEventListener('click', this.onDirectCheckout, true);
        this.form = form;
        this.form.addEventListener('submit', this.onSubmit, true);
        this.directCheckoutButton = this.form.querySelector('[data-optionsight-direct-checkout]');
        this.directCheckoutButton?.addEventListener('click', this.onDirectCheckout, true);
      }

      async onSubmit(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        await this.submitOptionSightPurchase({
          mode: 'cart',
          submitButton: event.submitter || this.form?.querySelector('[data-product-purchase-button]'),
        });
      }

      async onDirectCheckout(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (event.currentTarget?.disabled || event.currentTarget?.getAttribute('aria-disabled') === 'true') return;
        await this.submitOptionSightPurchase({ mode: 'checkout', submitButton: event.currentTarget });
      }

      async submitOptionSightPurchase({ mode, submitButton }) {
        if (this.purchaseInFlight) return;
        const checked = this.inputs.filter((input) => input.checked && !input.disabled);
        const completion = this.getRequiredCompletion(checked);
        if (!this.loaded || !completion.complete || !this.form) {
          this.renderFormError('Complete all required custom choices before continuing.');
          this.querySelector('[data-optionsight-option-group][data-optionsight-option-required="true"]:not(.is-complete)')
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.emitIntegrityDiagnostic('preflight', 'blocked', {
            mode,
            reason: this.loaded ? 'required_options_incomplete' : 'options_not_loaded',
          });
          return;
        }

        const formData = new FormData(this.form);
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        let payload;
        let expectation;
        let cartSnapshot = null;
        let addAttempted = false;

        try {
          payload = this.createCartPayload(formData, checked);
          expectation = this.createCartExpectation(payload);
        } catch (error) {
          this.emitIntegrityDiagnostic('preflight', 'failed', {
            mode,
            reason: this.getIntegrityErrorReason(error, 'invalid_expected_cart'),
          });
          this.renderFormError('Custom-option pricing could not be verified. Please refresh and try again.');
          return;
        }

        if (mode === 'cart' && cart) {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        this.purchaseInFlight = true;
        this.setSubmitLoading(submitButton, true);
        this.renderFormError('');
        this.emitIntegrityDiagnostic('preflight', 'passed', this.getDiagnosticSummary(mode, expectation));

        try {
          if (mode === 'checkout') {
            const independentCheckout = await this.createIndependentCheckout(payload);
            cartSnapshot = independentCheckout.cart;
            const verification = this.verifyCartExpectation(cartSnapshot, expectation);
            if (!verification.ok) {
              throw this.createIntegrityError(verification.reason, { cartSnapshot, verification });
            }

            const wholeCartVerification = this.verifyWholeCartIntegrity(cartSnapshot);
            if (!wholeCartVerification.ok) {
              throw this.createIntegrityError(wholeCartVerification.reason, {
                cartSnapshot,
                verification: wholeCartVerification,
              });
            }
            this.emitIntegrityDiagnostic('isolated-cart-verify', 'passed', {
              ...this.getDiagnosticSummary(mode, expectation),
              actualLineCount: verification.actualLineCount,
              actualAmountMinor: verification.actualAmountMinor,
              actualVariantIds: verification.actualVariantIds,
            });
            this.emitIntegrityDiagnostic('redirect', 'started', this.getDiagnosticSummary(mode, expectation));
            window.location.assign(independentCheckout.checkoutUrl);
            return;
          }

          addAttempted = true;
          const cartResponse = await this.addCartPayload(payload);
          this.emitIntegrityDiagnostic('cart-add', 'passed', this.getDiagnosticSummary(mode, expectation));

          cartSnapshot = await this.fetchCartSnapshot();
          const verification = this.verifyCartExpectation(cartSnapshot, expectation);
          if (!verification.ok) {
            throw this.createIntegrityError(verification.reason, { cartSnapshot, verification });
          }

          this.emitIntegrityDiagnostic('cart-verify', 'passed', {
            ...this.getDiagnosticSummary(mode, expectation),
            actualLineCount: verification.actualLineCount,
            actualAmountMinor: verification.actualAmountMinor,
            actualVariantIds: verification.actualVariantIds,
          });

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'optionsight-options',
              productVariantId: formData.get('id'),
              cartData: cartResponse,
            });
          }

          if (!cart) {
            window.location.assign(window.routes?.cart_url || '/cart');
            return;
          }
          cart.renderContents(this.normalizeCartResponse(cartResponse));
        } catch (error) {
          const reason = this.getIntegrityErrorReason(error, 'cart_integrity_failed');
          this.emitIntegrityDiagnostic('purchase', 'failed', {
            ...this.getDiagnosticSummary(mode, expectation),
            reason,
            ...(error.verification || {}),
          });

          const rollback = addAttempted
            ? await this.rollbackCartGroup(expectation.groupId, cartSnapshot, mode)
            : { confirmed: true, removedLineCount: 0 };
          const isolatedCheckoutFailed = mode === 'checkout' && !addAttempted;
          const requiresCartReview = !isolatedCheckoutFailed && (reason.startsWith('cart_wide_') || !rollback.confirmed);
          this.renderFormError(
            !requiresCartReview
              ? 'We could not verify all custom-option charges, so this item was not added. Please try again.'
              : 'We could not verify all custom-option charges. Please review your cart before trying again.'
          );
        } finally {
          this.purchaseInFlight = false;
          this.setSubmitLoading(submitButton, false);
          const currentChecked = this.inputs.filter((input) => input.checked && !input.disabled);
          this.setProductSubmitDisabled(!this.getRequiredCompletion(currentChecked).complete);
        }
      }

      async addCartPayload(payload) {
        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        let cartResponse;
        try {
          cartResponse = await response.json();
        } catch {
          throw this.createIntegrityError('cart_add_invalid_response');
        }
        if (response.ok === false || cartResponse?.status) {
          throw this.createIntegrityError('cart_add_rejected');
        }
        return cartResponse;
      }

      getIndependentCheckoutAttributes() {
        const attributes = window.LureMarketingAttribution?.getCurrentCartAttributes?.(window) || {};
        return Object.entries(attributes)
          .filter(([key, value]) => key && value !== undefined && value !== null && String(value) !== '')
          .map(([key, value]) => ({ key: String(key), value: String(value) }));
      }

      moneyAmountToMinor(amount) {
        const match = String(amount ?? '').match(/^(\d+)(?:\.(\d+))?$/);
        if (!match) return null;
        const fraction = `${match[2] || ''}00`.slice(0, 2);
        const minor = Number(match[1]) * 100 + Number(fraction);
        return Number.isSafeInteger(minor) ? minor : null;
      }

      normalizeStorefrontCart(cart) {
        const nodes = cart?.lines?.nodes;
        if (!Array.isArray(nodes)) throw this.createIntegrityError('isolated_cart_invalid');
        const items = nodes.map((line, index) => {
          const variantId = Number(String(line?.merchandise?.id || '').split('/').pop());
          const quantity = Number(line?.quantity);
          const originalPriceMinor = this.moneyAmountToMinor(line?.merchandise?.price?.amount);
          if (
            !Number.isSafeInteger(variantId)
            || variantId <= 0
            || !Number.isSafeInteger(quantity)
            || quantity < 1
            || !Number.isSafeInteger(originalPriceMinor)
          ) {
            throw this.createIntegrityError('isolated_cart_line_invalid');
          }
          return {
            id: variantId,
            variant_id: variantId,
            key: String(line?.id || `isolated-line-${index}`),
            quantity,
            properties: Object.fromEntries(
              (Array.isArray(line?.attributes) ? line.attributes : []).map((attribute) => [
                String(attribute?.key || ''),
                String(attribute?.value ?? ''),
              ])
            ),
            original_price: originalPriceMinor,
            original_line_price: originalPriceMinor * quantity,
            final_price: originalPriceMinor,
            final_line_price: originalPriceMinor * quantity,
          };
        });
        return { items };
      }

      async createIndependentCheckout(payload) {
        const storefrontDomain = String(this.dataset.storefrontDomain || '').trim();
        if (!/^[a-z0-9][a-z0-9.-]*\.myshopify\.com$/i.test(storefrontDomain)) {
          throw this.createIntegrityError('storefront_domain_invalid');
        }

        const lines = payload.items.map((item) => ({
          merchandiseId: `gid://shopify/ProductVariant/${item.id}`,
          quantity: Number(item.quantity || 1),
          attributes: Object.entries(item.properties || {}).map(([key, value]) => ({
            key: String(key),
            value: String(value ?? ''),
          })),
          ...(item.selling_plan
            ? { sellingPlanId: `gid://shopify/SellingPlan/${item.selling_plan}` }
            : {}),
        }));
        const countryCode = String(window.Shopify?.country || '').toUpperCase();
        const input = {
          lines,
          attributes: this.getIndependentCheckoutAttributes(),
          ...(/^[A-Z]{2}$/.test(countryCode) ? { buyerIdentity: { countryCode } } : {}),
        };
        const query = `mutation OptionSightCartCreate($input: CartInput!) {
          cartCreate(input: $input) {
            cart {
              checkoutUrl
              lines(first: 50) {
                nodes {
                  id
                  quantity
                  attributes { key value }
                  merchandise { ... on ProductVariant { id price { amount } } }
                }
              }
            }
            userErrors { field message }
            warnings { message }
          }
        }`;
        const response = await fetch(`https://${storefrontDomain}/api/2026-07/graphql.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ query, variables: { input } }),
        });
        let result;
        try {
          result = await response.json();
        } catch {
          throw this.createIntegrityError('isolated_cart_invalid_response');
        }
        const payloadResult = result?.data?.cartCreate;
        if (
          response.ok === false
          || result?.errors?.length
          || payloadResult?.userErrors?.length
          || payloadResult?.warnings?.length
          || !payloadResult?.cart?.checkoutUrl
        ) {
          throw this.createIntegrityError('isolated_cart_rejected');
        }
        return {
          checkoutUrl: String(payloadResult.cart.checkoutUrl),
          cart: this.normalizeStorefrontCart(payloadResult.cart),
        };
      }

      createCartPayload(formData, checkedInputs) {
        const quantity = Number(formData.get('quantity') || 1);
        const parentVariantId = String(formData.get('id') || '');
        const groupId = `optionsight-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const properties = {};

        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value && !match[1].startsWith('_optionsight_')) properties[match[1]] = value;
        }

        properties[this.dataset.propertyName || 'Selected custom options'] = checkedInputs
          .map((input) => input.dataset.optionLabel)
          .filter(Boolean)
          .join(', ');
        properties._optionsight_group_id = groupId;
        properties._optionsight_option_set = this.dataset.optionSetIds || '';

        const grouped = new Map();
        checkedInputs.forEach((input) => {
          const group = input.dataset.optionLabelOnCart || input.dataset.optionGroup || input.dataset.optionLabel;
          const label = input.dataset.optionLabel;
          if (!group || !label) return;
          grouped.set(group, [...(grouped.get(group) || []), label]);
        });
        grouped.forEach((values, key) => {
          properties[key] = values.join(', ');
        });

        const mainItem = { id: Number(parentVariantId), quantity, properties };
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) mainItem.selling_plan = sellingPlan;

        const addonsByVariantId = new Map();
        checkedInputs.forEach((input) => {
          const variantId = String(input.dataset.optionVariantId || '').trim();
          const isProductChoice = this.normalize(input.dataset.optionKind) === 'product';
          if (!variantId) {
            if (isProductChoice) throw this.createIntegrityError('addon_variant_missing');
            return;
          }
          const numericVariantId = Number(variantId);
          if (
            !Number.isSafeInteger(numericVariantId)
            || numericVariantId <= 0
            || !this.variantPricesMinor?.has(variantId)
          ) {
            throw this.createIntegrityError('addon_variant_invalid');
          }

          const group = String(input.dataset.optionGroup || '').trim();
          const label = String(input.dataset.optionLabel || '').trim();
          const addon = addonsByVariantId.get(variantId) || {
            id: numericVariantId,
            groups: new Set(),
            labels: new Set(),
          };
          if (group) addon.groups.add(group);
          if (label) addon.labels.add(label);
          addonsByVariantId.set(variantId, addon);
        });

        const addons = [...addonsByVariantId.values()]
          .map((addon) => ({
            id: addon.id,
            quantity,
            properties: {
              _optionsight_for: groupId,
              _optionsight_group_id: groupId,
              _optionsight_parent_variant_id: parentVariantId,
              _optionsight_option_set: this.dataset.optionSetIds || '',
              _optionsight_group: [...addon.groups].join(', '),
              _optionsight_label: [...addon.labels].join(', '),
            },
          }))
          .sort((left, right) => Number(left.id) - Number(right.id));

        const expectedAddons = addons.map((addon) => ({
          variantId: Number(addon.id),
          unitsPerParent: 1,
          unitPriceMinor: this.getAddOnUnitPriceMinor(addon.id),
        }));
        if (
          expectedAddons.some(
            (addon) =>
              !Number.isSafeInteger(addon.variantId)
              || !Number.isSafeInteger(addon.unitPriceMinor)
              || addon.unitPriceMinor < 0
          )
        ) {
          throw this.createIntegrityError('addon_price_unavailable');
        }

        properties._optionsight_integrity = 'v1';
        properties._optionsight_expected_count = String(expectedAddons.length);
        properties._optionsight_expected_amount = String(
          expectedAddons.reduce(
            (totalMinor, addon) => totalMinor + addon.unitsPerParent * addon.unitPriceMinor,
            0
          )
        );
        expectedAddons.forEach((addon, index) => {
          properties[`_optionsight_item_${index + 1}`] = [
            addon.variantId,
            addon.unitsPerParent,
            addon.unitPriceMinor,
          ].join('@');
        });

        return { items: [mainItem, ...addons] };
      }

      getAddOnUnitPriceMinor(variantId) {
        const priceMinor = this.variantPricesMinor?.get(String(variantId || ''));
        return Number.isSafeInteger(priceMinor) && priceMinor >= 0 ? priceMinor : null;
      }

      readParentVariantPrices() {
        const source = this.form?.querySelector('[data-optionsight-parent-variant-prices]');
        if (!source) return new Map();
        try {
          const rows = JSON.parse(source.textContent || '[]');
          return new Map(
            rows
              .map((row) => [String(row.id || ''), Number(row.priceMinor)])
              .filter(([id, priceMinor]) => id && Number.isSafeInteger(priceMinor) && priceMinor >= 0)
          );
        } catch {
          return new Map();
        }
      }

      createCartExpectation(payload) {
        const items = Array.isArray(payload?.items) ? payload.items : [];
        const [parent, ...addons] = items;
        const groupId = String(parent?.properties?._optionsight_group_id || '');
        const parentPrices = this.readParentVariantPrices();
        if (!parent || !groupId || items.length === 0) throw this.createIntegrityError('invalid_expected_cart');

        const expectedItems = items.map((item, index) => {
          const variantId = Number(item.id);
          const quantity = Number(item.quantity || 1);
          const unitPriceMinor =
            index === 0
              ? parentPrices.get(String(variantId))
              : this.getAddOnUnitPriceMinor(variantId);
          if (
            !Number.isSafeInteger(variantId)
            || !Number.isSafeInteger(quantity)
            || quantity < 1
            || !Number.isSafeInteger(unitPriceMinor)
            || unitPriceMinor < 0
          ) {
            throw this.createIntegrityError(index === 0 ? 'parent_price_unavailable' : 'addon_price_unavailable');
          }
          return {
            role: index === 0 ? 'parent' : 'addon',
            variantId,
            quantity,
            unitPriceMinor,
            originalLinePriceMinor: unitPriceMinor * quantity,
            properties: { ...(item.properties || {}) },
          };
        });

        return {
          groupId,
          items: expectedItems,
          originalAmountMinor: expectedItems.reduce(
            (totalMinor, item) => totalMinor + item.originalLinePriceMinor,
            0
          ),
        };
      }

      getCartJsonUrl() {
        const cartUrl = String(window.routes?.cart_url || '/cart').replace(/\/$/, '');
        return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
      }

      async fetchCartSnapshot() {
        const response = await fetch(this.getCartJsonUrl(), {
          method: 'GET',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          cache: 'no-store',
        });
        if (response.ok === false) throw this.createIntegrityError('cart_snapshot_rejected');
        try {
          const cart = await response.json();
          if (!Array.isArray(cart?.items)) throw this.createIntegrityError('cart_snapshot_invalid');
          return cart;
        } catch (error) {
          if (error?.optionsightReason) throw error;
          throw this.createIntegrityError('cart_snapshot_invalid');
        }
      }

      getCartLineVariantId(line) {
        return Number(line?.variant_id || line?.id || 0);
      }

      getCartGroupLines(cart, groupId) {
        return (Array.isArray(cart?.items) ? cart.items : []).filter((line) => {
          const properties = line?.properties || {};
          return properties._optionsight_group_id === groupId || properties._optionsight_for === groupId;
        });
      }

      verifyCartExpectation(cart, expectation) {
        const groupLines = this.getCartGroupLines(cart, expectation.groupId);
        const usedLineIndexes = new Set();
        const failures = [];

        expectation.items.forEach((expectedItem) => {
          const actualIndex = groupLines.findIndex((line, index) => {
            if (usedLineIndexes.has(index) || this.getCartLineVariantId(line) !== expectedItem.variantId) return false;
            const isAddon = Boolean(line?.properties?._optionsight_for);
            return isAddon === (expectedItem.role === 'addon');
          });
          if (actualIndex < 0) {
            failures.push('expected_line_missing');
            return;
          }

          usedLineIndexes.add(actualIndex);
          const actualLine = groupLines[actualIndex];
          const actualProperties = actualLine.properties || {};
          if (Number(actualLine.quantity) !== expectedItem.quantity) failures.push('line_quantity_mismatch');

          Object.entries(expectedItem.properties)
            .filter(([key]) => key.startsWith('_optionsight_'))
            .forEach(([key, value]) => {
              if (String(actualProperties[key] ?? '') !== String(value ?? '')) failures.push('line_group_mismatch');
            });

          const originalPriceMinor = Number(actualLine.original_price);
          const originalLinePriceMinor = Number(actualLine.original_line_price);
          if (originalPriceMinor !== expectedItem.unitPriceMinor) failures.push('line_price_mismatch');
          if (originalLinePriceMinor !== expectedItem.originalLinePriceMinor) failures.push('line_amount_mismatch');

          const finalPriceMinor = Number(actualLine.final_price);
          const finalLinePriceMinor = Number(actualLine.final_line_price);
          if (
            !Number.isFinite(finalPriceMinor)
            || !Number.isFinite(finalLinePriceMinor)
            || finalPriceMinor < 0
            || finalLinePriceMinor < 0
            || finalPriceMinor > originalPriceMinor
            || finalLinePriceMinor > originalLinePriceMinor
          ) {
            failures.push('discounted_line_amount_invalid');
          }
        });

        if (groupLines.length !== expectation.items.length || usedLineIndexes.size !== groupLines.length) {
          failures.push('group_line_count_mismatch');
        }

        const actualAmountMinor = groupLines.reduce(
          (totalMinor, line) => totalMinor + Number(line.original_line_price || 0),
          0
        );
        if (actualAmountMinor !== expectation.originalAmountMinor) failures.push('group_amount_mismatch');

        return {
          ok: failures.length === 0,
          reason: failures[0] || 'verified',
          actualLineCount: groupLines.length,
          actualAmountMinor,
          actualVariantIds: groupLines.map((line) => this.getCartLineVariantId(line)).filter(Boolean),
        };
      }

      parseCartParentIntegrity(parentLine) {
        const properties = parentLine?.properties || {};
        const expectedCount = Number(properties._optionsight_expected_count);
        const expectedAmountMinor = Number(properties._optionsight_expected_amount);
        if (
          properties._optionsight_integrity !== 'v1'
          || !Number.isSafeInteger(expectedCount)
          || expectedCount < 0
          || !Number.isSafeInteger(expectedAmountMinor)
          || expectedAmountMinor < 0
        ) {
          return null;
        }

        const itemKeys = Object.keys(properties)
          .filter((key) => /^_optionsight_item_\d+$/.test(key))
          .sort((left, right) => Number(left.split('_').pop()) - Number(right.split('_').pop()));
        if (itemKeys.length !== expectedCount) return null;

        const items = itemKeys.map((key, index) => {
          if (key !== `_optionsight_item_${index + 1}`) return null;
          const [variantIdValue, unitsValue, priceMinorValue, ...extra] = String(properties[key] || '').split('@');
          const variantId = Number(variantIdValue);
          const unitsPerParent = Number(unitsValue);
          const unitPriceMinor = Number(priceMinorValue);
          if (
            extra.length > 0
            || !Number.isSafeInteger(variantId)
            || variantId <= 0
            || !Number.isSafeInteger(unitsPerParent)
            || unitsPerParent <= 0
            || !Number.isSafeInteger(unitPriceMinor)
            || unitPriceMinor < 0
          ) {
            return null;
          }
          return { variantId, unitsPerParent, unitPriceMinor };
        });
        if (items.some((item) => !item)) return null;

        const stableVariantIds = items.map((item) => item.variantId);
        if (
          new Set(stableVariantIds).size !== stableVariantIds.length
          || stableVariantIds.some((variantId, index) => index > 0 && variantId <= stableVariantIds[index - 1])
        ) {
          return null;
        }
        const calculatedAmountMinor = items.reduce(
          (totalMinor, item) => totalMinor + item.unitsPerParent * item.unitPriceMinor,
          0
        );
        if (calculatedAmountMinor !== expectedAmountMinor) return null;
        return { expectedCount, expectedAmountMinor, items };
      }

      verifyWholeCartIntegrity(cart) {
        const cartItems = Array.isArray(cart?.items) ? cart.items : [];
        const optionPropertyName = this.dataset.propertyName || 'Selected custom options';
        const isAddonCandidate = (line) => {
          const properties = line?.properties || {};
          return Boolean(
            properties._optionsight_for
            || properties._optionsight_parent_variant_id
            || this.variantPricesMinor?.has(String(this.getCartLineVariantId(line)))
          );
        };
        const parents = cartItems.filter((line) => {
          const properties = line?.properties || {};
          const hasIntegrityMetadata = Object.keys(properties).some(
            (key) =>
              key === '_optionsight_integrity'
              || key === '_optionsight_expected_count'
              || key === '_optionsight_expected_amount'
              || /^_optionsight_item_\d+$/.test(key)
          );
          return (
            !isAddonCandidate(line)
            && (
              properties._optionsight_group_id
              || properties[optionPropertyName]
              || properties['Selected custom options']
              || hasIntegrityMetadata
            )
          );
        });
        const addons = cartItems.filter(isAddonCandidate);
        const failures = [];
        const knownGroupIds = new Set();

        parents.forEach((parentLine) => {
          const parentProperties = parentLine.properties || {};
          const groupId = String(parentProperties._optionsight_group_id || '');
          const parentVariantId = this.getCartLineVariantId(parentLine);
          const parentQuantity = Number(parentLine.quantity);
          if (!groupId || knownGroupIds.has(groupId)) {
            failures.push('cart_wide_parent_group_invalid');
            return;
          }
          knownGroupIds.add(groupId);

          const integrity = this.parseCartParentIntegrity(parentLine);
          if (!integrity || !Number.isSafeInteger(parentQuantity) || parentQuantity < 1) {
            failures.push('cart_wide_parent_metadata_invalid');
            return;
          }

          const groupAddons = addons.filter((line) => String(line.properties?._optionsight_for || '') === groupId);
          if (groupAddons.length !== integrity.expectedCount) failures.push('cart_wide_group_count_mismatch');
          const usedIndexes = new Set();

          integrity.items.forEach((expectedItem) => {
            if (this.getAddOnUnitPriceMinor(expectedItem.variantId) !== expectedItem.unitPriceMinor) {
              failures.push('cart_wide_live_price_mismatch');
            }
            const addonIndex = groupAddons.findIndex(
              (line, index) =>
                !usedIndexes.has(index) && this.getCartLineVariantId(line) === expectedItem.variantId
            );
            if (addonIndex < 0) {
              failures.push('cart_wide_expected_line_missing');
              return;
            }
            usedIndexes.add(addonIndex);
            const addonLine = groupAddons[addonIndex];
            const addonProperties = addonLine.properties || {};
            if (
              String(addonProperties._optionsight_group_id || '') !== groupId
              || String(addonProperties._optionsight_parent_variant_id || '') !== String(parentVariantId)
            ) {
              failures.push('cart_wide_mixed_group');
            }

            const expectedQuantity = parentQuantity * expectedItem.unitsPerParent;
            if (Number(addonLine.quantity) !== expectedQuantity) failures.push('cart_wide_quantity_mismatch');
            if (Number(addonLine.original_price) !== expectedItem.unitPriceMinor) {
              failures.push('cart_wide_price_mismatch');
            }
            if (Number(addonLine.original_line_price) !== expectedItem.unitPriceMinor * expectedQuantity) {
              failures.push('cart_wide_amount_mismatch');
            }
          });

          const actualAuxiliaryAmountMinor = groupAddons.reduce(
            (totalMinor, line) => totalMinor + Number(line.original_line_price || 0),
            0
          );
          if (actualAuxiliaryAmountMinor !== integrity.expectedAmountMinor * parentQuantity) {
            failures.push('cart_wide_group_amount_mismatch');
          }
        });

        addons.forEach((addonLine) => {
          const addonProperties = addonLine.properties || {};
          const parentGroupId = String(addonProperties._optionsight_for || '');
          if (
            !knownGroupIds.has(parentGroupId)
            || String(addonProperties._optionsight_group_id || '') !== parentGroupId
          ) {
            failures.push('cart_wide_orphan_or_mixed_addon');
          }
        });

        const optionSightLines = [...parents, ...addons];
        return {
          ok: failures.length === 0,
          reason: failures[0] || 'verified',
          actualLineCount: optionSightLines.length,
          actualAmountMinor: optionSightLines.reduce(
            (totalMinor, line) => totalMinor + Number(line.original_line_price || 0),
            0
          ),
          actualVariantIds: optionSightLines.map((line) => this.getCartLineVariantId(line)).filter(Boolean),
        };
      }

      async rollbackCartGroup(groupId, cartSnapshot, mode) {
        let cart = cartSnapshot;
        try {
          if (!cart) cart = await this.fetchCartSnapshot();
          const groupLines = this.getCartGroupLines(cart, groupId);
          const updates = {};
          groupLines.forEach((line) => {
            if (line?.key) updates[line.key] = 0;
          });

          if (Object.keys(updates).length > 0) {
            const response = await fetch(routes.cart_update_url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({ updates }),
            });
            if (response.ok === false) throw this.createIntegrityError('rollback_rejected');
          }

          const confirmedCart = await this.fetchCartSnapshot();
          const remainingLines = this.getCartGroupLines(confirmedCart, groupId);
          // A missing group cannot prove that a rejected/aborted add left no untagged line behind.
          // Only report a confirmed rollback after observing and then removing this exact group.
          const confirmed = groupLines.length > 0 && remainingLines.length === 0;
          this.emitIntegrityDiagnostic('rollback', confirmed ? 'passed' : 'failed', {
            mode,
            reason: confirmed ? 'group_removed' : 'group_still_present',
            groupId,
            removedLineCount: groupLines.length - remainingLines.length,
            remainingLineCount: remainingLines.length,
          });
          return { confirmed, removedLineCount: groupLines.length - remainingLines.length };
        } catch (error) {
          this.emitIntegrityDiagnostic('rollback', 'failed', {
            mode,
            reason: this.getIntegrityErrorReason(error, 'rollback_failed'),
            groupId,
          });
          return { confirmed: false, removedLineCount: 0 };
        }
      }

      createIntegrityError(reason, context = {}) {
        const error = new Error('OptionSight checkout integrity failure');
        error.optionsightReason = reason;
        Object.assign(error, context);
        return error;
      }

      getIntegrityErrorReason(error, fallback) {
        return String(error?.optionsightReason || fallback);
      }

      getDiagnosticSummary(mode, expectation) {
        return {
          mode,
          groupId: expectation.groupId,
          expectedLineCount: expectation.items.length,
          expectedAmountMinor: expectation.originalAmountMinor,
          expectedVariantIds: expectation.items.map((item) => item.variantId),
        };
      }

      emitIntegrityDiagnostic(stage, status, detail = {}) {
        const diagnostic = {
          version: 1,
          stage,
          status,
          mode: detail.mode,
          reason: detail.reason,
          groupId: detail.groupId,
          expectedLineCount: detail.expectedLineCount,
          actualLineCount: detail.actualLineCount,
          expectedAmountMinor: detail.expectedAmountMinor,
          actualAmountMinor: detail.actualAmountMinor,
          expectedVariantIds: detail.expectedVariantIds,
          actualVariantIds: detail.actualVariantIds,
          removedLineCount: detail.removedLineCount,
          remainingLineCount: detail.remainingLineCount,
        };
        Object.keys(diagnostic).forEach((key) => diagnostic[key] === undefined && delete diagnostic[key]);
        this.dispatchEvent(
          new CustomEvent('optionsight:checkout-integrity', {
            bubbles: true,
            detail: diagnostic,
          })
        );
        const log = status === 'failed' || status === 'blocked' ? console.error : console.info;
        log.call(console, '[OptionSight checkout integrity]', diagnostic);
        window.dataLayer?.push?.({ event: 'optionsight_checkout_integrity', ...diagnostic });
      }

      normalizeCartResponse(cartResponse) {
        if (!Array.isArray(cartResponse?.items)) return cartResponse;
        const parent = cartResponse.items.find((item) => !item?.properties?._optionsight_for) || cartResponse.items[0];
        return parent ? { ...cartResponse, id: parent.id, key: parent.key } : cartResponse;
      }

      setProductSubmitDisabled(disabled) {
        const form = document.getElementById(this.dataset.formId || '');
        const buttons = Array.from(
          form?.querySelectorAll('[data-product-purchase-button], [data-optionsight-direct-checkout]') || []
        );
        const productUnavailable = this.dataset.productUnavailable === 'true';
        const checkoutDisabled = disabled || productUnavailable;
        buttons.forEach((button) => {
          if (checkoutDisabled) {
            button.setAttribute('disabled', 'disabled');
            button.setAttribute('aria-disabled', 'true');
          } else {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
          }
        });

        const productForm = form?.closest('product-form');
        const acceleratedCheckout = form?.querySelector('[data-optionsight-accelerated-checkout]');
        acceleratedCheckout?.toggleAttribute('inert', checkoutDisabled);
        acceleratedCheckout?.setAttribute('aria-disabled', String(checkoutDisabled));
        productForm?.toggleAttribute('data-optionsight-checkout-ready', !checkoutDisabled);
      }

      setSubmitLoading(button, loading) {
        if (!button) return;
        if (loading) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
        button.classList.toggle('loading', loading);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      renderFormError(message) {
        const productForm = this.form?.closest('product-form');
        const wrapper = productForm?.querySelector('.product-form__error-message-wrapper');
        const text = wrapper?.querySelector('.product-form__error-message');
        if (!wrapper || !text) return;
        wrapper.toggleAttribute('hidden', !message);
        text.textContent = message || '';
      }

      uncheckConflictingOptions(selectedInput) {
        const conflicts = this.parseConflicts(selectedInput);
        if (!conflicts.length) return;
        this.inputs.forEach((input) => {
          if (input !== selectedInput && conflicts.includes(this.normalize(input.dataset.optionLabel))) input.checked = false;
        });
      }

      parseConflicts(input) {
        return (input.dataset.optionConflicts || '')
          .split(',')
          .map((value) => this.normalize(value))
          .filter(Boolean);
      }

      parseVisibilityRule(value) {
        if (!value) return null;
        try {
          return JSON.parse(value);
        } catch {
          return null;
        }
      }

      setSafeHtml(element, html, fallback) {
        const source = String(html || '').trim();
        if (!source) {
          element.textContent = fallback || '';
          return;
        }
        const template = document.createElement('template');
        template.innerHTML = source;
        this.sanitizeRichTextFragment(template.content);
        element.replaceChildren(template.content.cloneNode(true));
      }

      sanitizeRichTextFragment(fragment) {
        Array.from(fragment.querySelectorAll('*')).forEach((node) => {
          if (!OPTIONSIGHT_ALLOWED_RICH_TEXT_TAGS.has(node.tagName)) {
            node.replaceWith(...node.childNodes);
            return;
          }
          Array.from(node.attributes).forEach((attribute) => {
            if (node.tagName === 'A' && ['href', 'title', 'target', 'rel'].includes(attribute.name)) return;
            node.removeAttribute(attribute.name);
          });
          if (node.tagName === 'A') {
            const href = node.getAttribute('href') || '';
            if (!/^(https?:|mailto:|tel:|\/|#)/i.test(href)) node.removeAttribute('href');
            if (node.getAttribute('target') === '_blank') node.setAttribute('rel', 'noopener noreferrer');
          }
        });
      }

      getRowKind(selector) {
        const value = String(selector || '').trim();
        if (value === 'property' || value === 'heading' || value === 'note' || value === 'divider') return value;
        if (value.startsWith('variant:') || /^\d{8,}$/.test(value)) return 'product';
        return 'property';
      }

      isChoiceKind(kind) {
        return kind === 'property' || kind === 'product';
      }

      getGroupPresentation(row, group) {
        const style = this.normalize(row.displayStyle);
        if (style === 'vertical') return 'vertical';
        if (style === 'horizontal') return 'horizontal';
        if (/functions?\d*|autosuck/i.test(group)) return 'vertical';
        return 'horizontal';
      }

      getGroupTitle(group) {
        const labels = {
          EyeColour: 'Customize Your Doll’s Eye Colour',
          Eyecolour: 'Customize Your Doll’s Eye Colour',
          Skincolour: 'Doll’s Skin Colour',
          SkinColour: 'Doll’s Skin Colour',
          NippleColour: 'Nipple Colour',
          Nipplecolour: 'Nipple Colour',
          AreolaSize: 'Areola Size',
          Areolasize: 'Areola Size',
          AreolaSzie: 'Areola Size',
          FingerNail: 'Fingernail Colour',
          PublicHair: 'Pubic Hair Option',
          PubicHair: 'Pubic Hair Option',
          PremiumSkin: 'Premium Skin',
          PremiumOptions: 'Premium Options',
          Functions1: 'Premium Functions 1',
          Functions2: 'Premium Functions 2',
          Functions3: 'Premium Functions 3',
        };
        return labels[group] || group;
      }

      formatOptionList(labels) {
        const values = [...new Set((labels || []).filter(Boolean))];
        if (values.length <= 1) return values[0] || 'This option';
        if (values.length === 2) return `${values[0]} and ${values[1]}`;
        return `${values.slice(0, -1).join(', ')}, and ${values.at(-1)}`;
      }

      formatPrice(value) {
        const source = String(value || '').trim();
        if (!source) return '';
        if (/free/i.test(source)) return 'Free';
        const amount = this.parsePriceAmount(source);
        if (amount <= 0) return source;
        return `(+ ${this.formatMoney(amount)} ${this.getCurrency()})`;
      }

      formatPreviewLabel(label, price) {
        const amount = this.parsePriceAmount(price);
        if (amount <= 0) return String(label || '').trim();
        return `+${this.formatMoney(amount)} ${this.getCurrency()} · ${String(label || '').trim()}`;
      }

      parsePriceAmount(value) {
        const source = String(value || '').trim();
        if (!source || /free/i.test(source)) return 0;
        const match = source.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
        return match ? Math.max(0, Number(match[0])) : 0;
      }

      getCurrency() {
        return window.Shopify?.currency?.active || 'USD';
      }

      formatMoney(amount) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.getCurrency(),
        }).format(amount);
      }

      parseDimensions(value, fallbackWidth, fallbackHeight) {
        const match = String(value || '').match(/(\d+)x(\d+)/i);
        return match ? [Number(match[1]), Number(match[2])] : [fallbackWidth, fallbackHeight];
      }

      safeId(value) {
        return String(value || 'option').replace(/[^a-z0-9_-]/gi, '-');
      }

      normalize(value) {
        return decodeOptionSightOptionEntities(value).trim().toLowerCase();
      }
    }
  );
}
